import {
  CallEndedStateFields,
  CallConnectionStateFields,
  hasAnsweredConnection,
  isCallEndedForUi,
  isConnectionActive,
} from '@act/shared/utilities';

/** Action the minimized dock should take after inspecting call + actor state. */
export type MinimizedDockDismissAction =
  | 'restoreFullscreen'
  | 'clearDockOnly'
  | null;

/** Connection fields needed to decide whether the acting user left the call. */
export interface MinimizedDockConnectionFields
  extends CallConnectionStateFields {
  userId?: string | null;
}

/** Call snapshot used for minimized-dock auto-dismiss. */
export interface MinimizedDockCallFields extends CallEndedStateFields {
  connections?: MinimizedDockConnectionFields[] | null;
}

/**
 * Decide how to clear the minimized call dock.
 *
 * - Whole call ended → restore fullscreen (post-call UI).
 * - Acting user left while the conference continues → clear dock only and
 *   unmount the Phone Call dialog (no restore / slide-up), so Leave/End
 *   cannot leave a stuck banner or bounce the guide back into call UI.
 * - Still on call / unknown legs → no dismiss.
 *
 * FAILED is terminal in CallStatusMapper even when only one leg failed while
 * another remains connected — same caveat as the fullscreen header. Do not
 * restore while a leg is still live.
 *
 * @param call - Minimized call snapshot.
 * @param actingUserId - Current acting user id.
 * @returns Dock dismiss action, or null when the dock should stay.
 */
export function getMinimizedDockDismissAction(
  call: MinimizedDockCallFields | null | undefined,
  actingUserId: string | undefined
): MinimizedDockDismissAction {
  if (!call) {
    return null;
  }

  const connections = call.connections ?? [];
  // Prefer live legs over a terminal status: FAILED can mean a sibling leg
  // died while the conference continues (see getCallProgressionStatus).
  const isLiveConnected =
    hasAnsweredConnection(connections) && !call.timeEnded;

  if (isCallEndedForUi(call) && !isLiveConnected) {
    return 'restoreFullscreen';
  }

  if (!actingUserId) {
    return null;
  }

  const userLegs = connections.filter(
    connection => connection.userId === actingUserId
  );

  // Avoid false dismiss before the acting user's leg is present on the DTO.
  if (userLegs.length === 0) {
    return null;
  }

  if (userLegs.some(isConnectionActive)) {
    return null;
  }

  return 'clearDockOnly';
}
